"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";
import i18n from "@/lib/i18n";

/** Interval between HEAD staleness polls (milliseconds). */
const POLL_INTERVAL_MS = 5000;

/** State of an HTML-file fetch. */
interface HtmlFileState {
  content: string | null;
  loading: boolean;
  error: string | null;
  /** True briefly after a background refresh delivers new content. */
  refreshed: boolean;
}

/**
 * Fetch an HTML file's raw text from a signed file-download URL.
 *
 * After the initial GET succeeds, a lightweight HEAD request checks ETag and
 * Last-Modified every five seconds. When either changes, the hook silently
 * re-fetches the raw document so the existing iframe remains visible until
 * replacement content is ready.
 */
export function useHtmlFile(
  url: string | null,
): HtmlFileState & { refetch: () => void } {
  const [state, setState] = useState<HtmlFileState>({
    content: null,
    loading: false,
    error: null,
    refreshed: false,
  });
  const [refetchTick, setRefetchTick] = useState(0);
  const etagRef = useRef<string | null>(null);
  const lastModifiedRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const activeUrlRef = useRef<string | null>(null);

  const doFetch = useCallback((fetchUrl: string) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    let cancelled = false;

    setState((prev) => ({
      ...prev,
      content: null,
      loading: true,
      error: null,
      refreshed: false,
    }));

    fetch(fetchUrl, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) {
          const message =
            fetchUrl.endsWith("/preview") && [401, 404, 405, 501, 502].includes(res.status)
              ? i18n.t("chat:singleFile.xlsxPreviewUnavailable")
              : fetchUrl.endsWith("/preview")
                ? i18n.t("chat:singleFile.xlsxPreviewFailed", { status: res.status })
                : res.status === 410
              ? i18n.t("fileErrors.loadExpired")
              : res.status === 404
                ? i18n.t("fileErrors.loadNotFound")
                : res.status === 502
                  ? i18n.t("fileErrors.routingError")
                  : i18n.t("fileErrors.loadHttp", { status: res.status });
          throw new Error(message);
        }
        if (fetchUrl.endsWith("/preview") && res.headers.get("x-l9-xlsx-preview") !== "1") {
          throw new Error(i18n.t("chat:singleFile.xlsxPreviewUnavailable"));
        }
        etagRef.current = res.headers.get("etag");
        lastModifiedRef.current = res.headers.get("last-modified");
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        setState({ content: text, loading: false, error: null, refreshed: false });
      })
      .catch((err) => {
        if (cancelled) return;
        if (
          controller.signal.aborted ||
          (err instanceof DOMException && err.name === "AbortError")
        ) {
          return;
        }
        setState({
          content: null,
          loading: false,
          error: fetchUrl.endsWith("/preview") && err instanceof TypeError
            ? i18n.t("chat:singleFile.xlsxPreviewUnavailable")
            : err instanceof Error ? err.message : i18n.t("fileErrors.loadGeneric"),
          refreshed: false,
        });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const doRefresh = useCallback((fetchUrl: string) => {
    const controller = new AbortController();
    let cancelled = false;

    fetch(fetchUrl, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) return;
        etagRef.current = res.headers.get("etag");
        lastModifiedRef.current = res.headers.get("last-modified");
        return res.text();
      })
      .then((text) => {
        if (cancelled || text == null) return;
        setState((prev) => {
          if (prev.content === text) return prev;
          setTimeout(() => {
            setState((current) => ({ ...current, refreshed: false }));
          }, 2000);
          return { ...prev, content: text, refreshed: true };
        });
      })
      .catch(() => {
        // A background refresh should not disrupt the displayed document.
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const doHeadPoll = useCallback(
    (pollUrl: string) => {
      const controller = new AbortController();
      let cancelled = false;

      fetch(pollUrl, { method: "HEAD", signal: controller.signal })
        .then((res) => {
          if (cancelled || !res.ok) return;
          const newEtag = res.headers.get("etag");
          const newLastModified = res.headers.get("last-modified");
          const changed =
            (newEtag != null && newEtag !== etagRef.current) ||
            (newLastModified != null && newLastModified !== lastModifiedRef.current);
          if (changed) {
            etagRef.current = newEtag;
            lastModifiedRef.current = newLastModified;
            doRefresh(pollUrl);
          }
        })
        .catch(() => {
          // The next polling cycle can retry a failed staleness check.
        });

      return () => {
        cancelled = true;
        controller.abort();
      };
    },
    [doRefresh],
  );

  useEffect(() => {
    if (!url) {
      setState({ content: null, loading: false, error: null, refreshed: false });
      activeUrlRef.current = null;
      return;
    }

    activeUrlRef.current = url;
    etagRef.current = null;
    lastModifiedRef.current = null;
    return doFetch(url);
  }, [url, refetchTick, doFetch]);

  useEffect(() => {
    if (!url || state.error || state.content == null) return;

    const intervalId = setInterval(() => {
      if (activeUrlRef.current !== url) return;
      doHeadPoll(url);
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [url, state.content, state.error, doHeadPoll]);

  const refetch = useCallback(() => {
    setRefetchTick((tick) => tick + 1);
  }, []);

  useRefreshOnReconnect(refetch, Boolean(url && state.error));

  return { ...state, refetch };
}
